import React from 'react';

interface TouchAnalysisProps {
  attackMatch?: number; // 0.5 (trop mou) -> 1.0 (match) -> 1.5 (trop fort)
  lang?: 'en' | 'fr';
}

export const MFCCVisualizer: React.FC<TouchAnalysisProps> = ({ attackMatch = 1.0, lang = 'fr' }) => {
  const isFr = lang === 'fr';
  
  // Axe 7: Render placeholder if no meaningful data (attackMatch is 0 or undefined)
  if (attackMatch === 0 || attackMatch === undefined) {
    return (
        <div className="bg-[#000507] p-10 rounded-[60px] border border-white/10 shadow-2xl h-full flex flex-col gap-8 relative overflow-hidden group justify-center items-center opacity-40 text-center">
            <span className="text-6xl mb-4">🖐️</span>
            <h3 className="text-xl font-black text-white uppercase italic">Analyse d'Attaque Indisponible</h3>
            <p className="text-[10px] text-slate-500 uppercase font-bold max-w-xs leading-relaxed">
                Le système n'a pas pu évaluer la dynamique de votre médiator. Assurez-vous d'avoir des attaques claires.
            </p>
        </div>
    );
  }

  // Interprétation pédagogique
  const getInterpretation = () => {
      if (attackMatch > 1.3) return {
          title: isFr ? "Attaque Trop Lourde" : "Heavy Picking",
          advice: isFr ? "Tu écrases trop tes cordes. Allège ton coup de médiator pour laisser respirer la note." : "You're hitting too hard. Lighten your touch to let the note breathe.",
          color: "text-red-500",
          bg: "bg-red-500/10",
          icon: "🔨"
      };
      if (attackMatch < 0.7) return {
          title: isFr ? "Attaque Trop Molle" : "Soft Attack",
          advice: isFr ? "Manque de conviction ! Précise ton attaque avec le bout du médiator pour plus de clarté." : "Lack of punch! Use the tip of the pick for more clarity.",
          color: "text-cyan-400",
          bg: "bg-cyan-500/10",
          icon: "☁️"
      };
      return {
          title: isFr ? "Toucher Identique" : "Pro Touch",
          advice: isFr ? "Ta dynamique d'attaque est parfaitement alignée avec le Master. Travail de pro." : "Your attack dynamics are perfectly aligned with the Master. Pro work.",
          color: "text-emerald-400",
          bg: "bg-emerald-500/10",
          icon: "✨"
      };
  };

  const ui = getInterpretation();

  return (
    <div className="bg-[#000507] p-10 rounded-[60px] border border-white/10 shadow-2xl h-full flex flex-col gap-8 relative overflow-hidden group">
      <div className="space-y-1">
        <h3 className="text-3xl font-black text-white italic uppercase tracking-tighter">Touch <span className="text-smart-gold">Analysis</span></h3>
        <p className="text-[10px] text-slate-500 font-black uppercase tracking-[0.4em] ml-1">Analyse dynamique de l'attaque</p>
      </div>

      <div className={`flex-1 flex flex-col items-center justify-center p-8 rounded-[40px] border border-white/5 ${ui.bg} transition-all duration-700`}>
          <div className="text-7xl mb-6 filter drop-shadow-[0_0_20px_rgba(255,255,255,0.2)] group-hover:scale-110 transition-transform">
            {ui.icon}
          </div>
          <div className={`text-2xl font-black uppercase italic tracking-tight mb-2 ${ui.color}`}>
            {ui.title}
          </div>
          <p className="text-sm text-slate-300 font-medium italic text-center max-w-xs leading-relaxed">
            "{ui.advice}"
          </p>
      </div>

      <div className="bg-black/40 p-6 rounded-3xl border border-white/5 space-y-4">
          <div className="flex justify-between items-center text-[8px] font-black text-slate-500 uppercase tracking-widest">
            <span>MOU</span>
            <span>MATCH MASTER</span>
            <span>DUR</span>
          </div>
          <div className="h-2 w-full bg-white/5 rounded-full relative overflow-hidden">
             {/* Cursor */}
             <div 
                className={`absolute top-0 h-full w-4 -ml-2 rounded-full transition-all duration-1000 shadow-[0_0_15px_white] ${ui.color.replace('text', 'bg')}`}
                style={{ left: `${Math.min(100, Math.max(0, (attackMatch / 2) * 100))}%` }}
             />
             {/* Master Zone */}
             <div className="absolute left-[45%] right-[45%] top-0 h-full bg-white/10 border-x border-white/20"></div>
          </div>
      </div>
    </div>
  );
};